<template>
  <div class="data-list-wrapper">
    <div id="common-data-table" class="base-table-wrapper">
      <v-data-table
        :value="dataTableSelected"
        class="base-table sc-data-table"
        :items="itemsEntry"
        :headers="headersDataEntry"
        :options="options"
        :server-items-length="totalRowsEntry"
        :height="calcTableHeight || 'auto'"
        :show-select="allowSelect"
        :single-select="singleSelect"
        :loading="loading"
        :fixed-header="fixedHeaders"
        :mobile-breakpoint="0"
        hide-default-footer
        :item-key="itemKey"
        :disable-sort="disableSort"
        @toggle-select-all="onToggleSelectAll"
      >
        <template #header.multi="{ header }">
          <template v-for="item in header.multi">
            <CustomFieldSelect
              v-if="item.value === 'custom_field'"
              :key="item.value"
              :custom-field="customField"
              :custom-field-items="customFieldItems"
              :source="source"
              @change:selected="resetSorting"
            >
              <template #default="{ label, value }">
                <span
                  class="custom-column column sortable header-span"
                  :class="{ [item.class]: item.class, 'active': isActiveSort({ value }) }"
                  @click="setSort({ value, sort: getSorting({ value }), text: label })"
                >
                  <span class="mr-1">{{ label }}</span>
                  <v-btn
                    icon
                    x-small
                    :color="isActiveSort({ value }) && !options.sortDesc[0] ? 'black' : 'grey'"
                  >
                    <v-icon v-if="item.icon" small>
                      {{ generateIconName(isAscendingSorting({ value })) }}
                    </v-icon>
                  </v-btn>
                </span>
              </template>
            </CustomFieldSelect>
            <div
              v-else-if="item.sortable && !disableSort"
              :key="item.text"
              style="white-space: nowrap"
              class="text-capitalize"
            >
              <span
                class="custom-column column sortable header-span"
                :class="{ [item.class]: item.class, 'active': isActiveSort(item) }"
                @click="setSort({ value: item.value, sort: getSorting(item), text: item.text })"
              >
                <span class="mr-1">{{ item.text }}</span>
                <v-btn
                  icon
                  x-small
                  :color="isActiveSort(item) && !options.sortDesc[0] ? 'black' : 'grey'"
                >
                  <v-icon v-if="item.icon" small>
                    {{ generateIconName(isAscendingSorting(item)) }}
                  </v-icon>
                </v-btn>
                <AppTooltip v-if="item.tooltip" :text="item.tooltip" wrap>
                  <template #default="{ on, attrs }">
                    <v-icon
                      size="14"
                      data-test="header-tooltip"
                      v-bind="attrs"
                      v-on="on"
                      @click.stop
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                </AppTooltip>
              </span>
            </div>
            <div
              v-else
              :key="item.text"
              style="white-space: nowrap"
              :class="item.class ? item.class : ''"
              class="text-capitalize"
            >
              <div v-if="item.multi">
                <template v-if="item.groupName">
                  {{ item.groupName }}:
                </template>
                <span v-for="(subItem) in item.multi" :key="subItem.value" class="sub-item">
                  <span
                    v-if="subItem.sortable && !disableSort"
                    :key="subItem.text"
                    style="white-space: nowrap"
                    class="column sortable header-span column--pointer mr-1"
                    :class="{ [subItem.class]: subItem.class, 'active': isActiveSort(subItem) }"
                    @click="setSort({ value: subItem.value, sort: getSorting(subItem), text: subItem.text })"
                  >
                    <div class="d-flex align-center">
                      {{ subItem.text }}<template v-if="subItem.text === 'City' && !isActiveSort(subItem)">,&nbsp;</template><template v-else-if="subItem.text === 'State' && !isActiveSort(subItem)">,&nbsp;</template>
                      <v-btn v-if="isActiveSort(subItem) && subItem.icon" icon x-small>
                        <v-icon small>
                          {{ generateIconName(isAscendingSorting(subItem)) }}
                        </v-icon>
                      </v-btn>
                      <template v-if="subItem.text === 'City' && isActiveSort(subItem)">,&nbsp;</template>
                    </div>
                  </span>
                  <span v-else>
                    {{ subItem.text }}
                  </span>
                </span>
              </div>
              <span v-else>{{ item.text }}</span>
            </div>
          </template>
        </template>
        <template #header.views="{ header }">
          <span class="views-span hidden-screen-only text-capitalize">
            {{ header.text }}
          </span>
        </template>
        <template #top>
          <v-fade-transition>
            <div v-show="selected.length" class="pa-2" style="background-color: #EEEEEE;">
              <span v-if="selected.length === totalRowsEntry">
                All <span class="base-table__number">{{ selected.length }}</span> items are selected.
              </span>
              <span v-if="selected.length !== totalRowsEntry">
                <span class="base-table__number">{{ selected.length }}</span> items on this page are selected.
                <v-progress-circular
                  v-if="loading"
                  color="primary"
                  indeterminate
                  size="20"
                  class="ml-1"
                  width="2"
                />
                <span v-else-if="allowShowCheckAll" class="base-table__check-all" @click="$emit('checkAll')">
                  Select all {{ totalRowsEntry }} items
                </span>
                <v-btn icon title="Clear Selected" @click="unCheckAll"><v-icon>mdi-close-circle</v-icon></v-btn>
              </span>
            </div>
          </v-fade-transition>
        </template>
        <template #item="{ item }">
          <slot
            name="item"
            :item="item"
          />
        </template>
        <template #body.append="{ items }">
          <slot v-if="items.length" name="footer-summary" />
        </template>
      </v-data-table>
    </div>
    <v-toolbar v-show="!hidePagination" class="base-table-pagination">
      <v-pagination
        v-model="options.page"
        :length="pageCount || 0"
        :disabled="pageCount === 0 || loading"
        :total-visible="$vuetify.breakpoint.xs ? 3 : 7"
        class="pa-2"
        width="800"
      />
      <v-menu offset-y>
        <template #activator="{ on, attrs }">
          <v-btn
            text
            v-bind="attrs"
            :small="$vuetify.breakpoint.xs"
            :class="{ 'px-1': $vuetify.breakpoint.xs }"
            v-on="on"
          >
            {{ options.itemsPerPage }} per page
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in options.itemsPerPageOptions"
            :key="index"
            link
            @click="updatePerPage(item)"
          >
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';

import { ITableOptions, IHeader } from '@/bundles/BaseTable/interfaces';
import { defaultItemsPerPageOptions } from '@/bundles/BaseTable/helpers/consts';
import { ITextValue } from '@/types/common';
import { SourceTypeEnum } from '@/bundles/App/enums';

import AppTooltip from '@/bundles/App/components/common/AppTooltip.vue';

import CustomFieldSelect from './CustomFieldSelect.vue';

interface IBaseTableInstance {
  options: ITableOptions,
  footerOptions: {
    itemsPerPageText: string;
    disableItemsPerPage: boolean;
    itemsPerPageOptions: number[];
    showFirstLastPage: boolean;
  };
}

export default Vue.extend({
  name: 'BaseTable',

  components: {
    AppTooltip,
    CustomFieldSelect,
  },

  props: {
    tableOptionsEntry: {
      type: Object as PropType<ITableOptions>,
      required: true,
    },
    itemsEntry: {
      type: Array,
      default: () => [],
    },
    totalRowsEntry: {
      type: Number,
      default: 0,
    },
    headersDataEntry: {
      type: Array as PropType<IHeader[]>,
      required: true,
    },
    selected: {
      type: Array,
      default: () => [],
    },
    tableHeight: {
      type: Number,
      default: null,
    },
    allowSelect: Boolean,
    loading: Boolean,
    fixedHeaders: Boolean,
    hidePagination: Boolean,
    allowShowCheckAll: {
      type: Boolean,
      default: true,
    },
    disableSort: Boolean,
    isMergeTable: Boolean,
    itemKey: {
      type: String,
      default: '_key',
    },
    customField: {
      type: String,
      default: null,
    },
    customFieldItems: {
      type: Array as PropType<ITextValue<string>[]>,
      default: () => [] as ITextValue<string>[],
    },
    source: {
      type: String as PropType<string | SourceTypeEnum>,
      default: null,
    },
    singleSelect: Boolean,
  },

  data: (): IBaseTableInstance => ({
    options: {
      groupBy: [],
      itemsPerPage: 50,
      multiSort: false,
      mustSort: false,
      page: 1,
      sortBy: [],
      sortDesc: [],
      itemsPerPageOptions: defaultItemsPerPageOptions
    },
    footerOptions: {
      itemsPerPageText: '',
      disableItemsPerPage: true,
      itemsPerPageOptions: [10, 25, 100],
      showFirstLastPage: true,
    },
  }),

  computed: {
    pageCount: function (): number {
      if (this.totalRowsEntry <= this.options.itemsPerPage) {
        return 1;
      }

      return Math.ceil(this.totalRowsEntry / this.options.itemsPerPage);
    },
    calcTableHeight: function (): number {
      if (this.selected.length && this.tableHeight) {
        return this.tableHeight - 40;
      }

      return this.tableHeight;
    },
    dataTableSelected (): Record<string, any>[] {
      const isSelectedObjects = this.selected.every((item) => typeof item === 'object' && item !== null);
      if (isSelectedObjects) {
        return this.selected as Record<string, any>[];
      }

      return this.selected.map((item) => ({ [this.itemKey]: item }));
    }
  },

  watch: {
    options: {
      handler: function (value) {
        this.$emit('options:change', value);
      },
      deep: true,
    },
    tableOptionsEntry: {
      handler: function (value) {
        if (JSON.stringify(value) !== JSON.stringify(this.options)) {
          this.options = { ...value };
        }
      },
      deep: true,
      immediate: true
    },
  },

  created () {
    this.$eventBus.$emit('refresh:table-height');
  },

  mounted () {
    this.$eventBus.$on('filter-change', this.unCheckAll);
  },

  beforeDestroy () {
    this.$eventBus.$off('filter-change', this.unCheckAll);
  },

  methods: {
    updatePerPage (item) {
      this.options.itemsPerPage = item.value
      this.options.page = 1
    },
    generateIconName: function (isAscending: boolean): string {
      return `mdi-arrow-${ isAscending ? 'up' : 'down' }-thin`;
    },
    isActiveSort: function (item: IHeader): boolean {
      const sortBy = Array.isArray(this.options.sortBy)
        ? this.options.sortBy[0]
        : this.options.sortBy;
      const valueText = item.value;
      return sortBy === valueText;
    },
    setSort: function ({ value, sort, text }: { value: string; sort: boolean, text: string }) {
      this.options = { ...this.options, sortBy: [value], sortDesc: [sort], sortText: text };
      document.querySelector('.base-table .v-data-table__wrapper')!.scrollTop = 0
    },

    onToggleSelectAll: function (event) {
      const selectAllEventName = this.isMergeTable ? 'checkAll' : 'changeSelected';
      const eventName = event.value ? selectAllEventName : 'uncheckAll';

      this.$emit(eventName, event.items);
    },
    unCheckAll () {
      this.$emit('uncheckAll');
    },
    getSorting (item: IHeader): boolean {
      return this.isActiveSort(item) ? !this.options.sortDesc?.[0] : true;
    },
    isAscendingSorting (item: IHeader): boolean {
      return this.isActiveSort(item) ? !this.options.sortDesc?.[0] : false;
    },
    resetSorting () {
      this.options = { ...this.options, sortBy: [], sortDesc: [], sortText: undefined };
    }
  },
});
</script>

<style lang="scss" scoped>
.base-table-wrapper {
  padding-bottom: 0;
  overflow: auto;
}

.base-table {
  &__check-all {
    cursor: pointer;
    color: #1E88E5;
    transition: all ease .3s;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
  }

  &__number {
    font-weight: 600;
    color: #1E88E5;
  }
}

.custom-column {
  &.sortable {
    &.active {
      color: rgba(0,0,0,.87)!important;
    }
  }
}

.column {
  &--pointer {
    cursor: pointer;
  }
}

.sub-item {
  display: inline-block;
  margin-left: 4px;

  &:first-of-type {
    margin-left: 0;
  }
}
</style>
